import React, { useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import { useIntl } from 'react-intl';
import { Field, Form, Formik } from 'formik';

// Models && Types
import { PassengerModel } from '../../models/PassengerModel';
import { PersonModel } from '../../models/PersonModel';
import { PassengerPageLocationState } from '../../models/PassengerPageLocationState';

// Components
import StepNavigation from '../../components/StepNavigation';
import Checkbox from '../../components/fields/Checkbox';
import SignaturePad from '../../components/fields/SignaturePad';
import Card from '../../components/Card';
import StepTitle from '../../components/StepTitle';
import Loading from '../../components/Loading';
import PermissionDescription from '../Permission/PermissionDescription';
import PassengerDataForm from '../../components/PassengerDataForm';
import TrustBox from '../../components/TrustBox';

// Utils
import useSortFields from '../../hooks/useSortFields';
import { useHandleError } from '../../services/api';
import { validateForm } from '../Passengers/PassengerForm';
import { legalGuardianHandleSubmit } from './LegalGuardianHandleSubmit';
import { useBuild, useJsonApi } from '../../services/GlobalStore/GlobalStore';
import routes from '../../messages/routes';
import useAuthenticate from '../../hooks/useAuthenticate';
import messages from './messages';
import { PathDefs, useStepNavigate } from '../../hooks/useStepNavigate';
import { PageType } from '../Passengers/PassengerContainer';
import { getIdFromNumber } from '../ClaimPage/Claim';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import {
  steps,
  translateRoute,
  useTranslatedNavigate,
} from '../../utils/routes';
import {
  DocumentPathResolver,
  useDocumentsNavigate,
} from '../../hooks/useDocumentsNavigate';

/**
 * Resolves next route on AdultPassenger and LegalGuardian pages
 * Should redirect to NEXT passenger or mail-upload screen if no next guardian.
 * This function covers only a case there is no next guardian.
 */
export const resolveNextPassengerRoute = async (
  pathname: string,
  nextPassengerId?: string | number,
  // eslint-disable-next-line
  resolver?: () => ReturnType<DocumentPathResolver>
): Promise<string> => {
  if (nextPassengerId)
    return pathname.replace(
      new RegExp(translateRoute(routes[steps.Passenger]) + '.+$'),
      translateRoute(routes[steps.Passenger]) + `/${nextPassengerId}`
    );
  else if (resolver) {
    const nextPath = await resolver();
    return pathname.replace(/(^\/\w+\/\d+)($|.+)/, (match, g1) =>
      [g1, translateRoute(nextPath)].join('/')
    );
  } else return '';
};

/**
 * Checks if claim jurisdiction needs two parents
 * 55 - Germany
 * 160 - Netherlands
 * @param placeOfJurisdictionId
 */
export const requiresTwoGuardians = (placeOfJurisdictionId: string): boolean =>
  ['55', '160'].includes(placeOfJurisdictionId);

const nameFields = ['first_name', 'last_name'];

const LegalGuardian = ({
  pageType,
  claimNumber,
  passengerId,
  guardianIndex,
}: {
  pageType: PageType;
  passengerId: string;
  guardianIndex: string;
  claimNumber: string;
}): JSX.Element => {
  const [handleError] = useHandleError();
  const translatedNavigate = useTranslatedNavigate();
  const { formatMessage } = useIntl();
  const { token } = useAuthenticate();
  const { navigateNext: navigateToNextStep } = useStepNavigate(
    PathDefs.AccountClaimDocumentsPassengerAuthorizationGuardian
  );

  const {
    state,
    pathname,
  }: { state: PassengerPageLocationState; pathname: string } = useLocation();
  const minorPassenger: PassengerModel = useBuild('passengers', passengerId);

  // Safe the legal Guardian in a state necessary for loading logic for form
  const [legalGuardian, setLegalGuardian] = useState<PersonModel>();
  const [
    legalGuardianRequiredFields,
    setLegalGuardianRequiredFields,
  ] = useState([]);
  const [missingFields, setMissingFields] = useState([]);

  // Load the dbMinorPerson with the legalGuardians could be one or more legalGuardians
  const { data: dbMinorPerson, isLoaded: loadedMinorPerson } = useJsonApi(
    `/persons/${minorPassenger?.person?.id}`,
    {
      cachePolicy: 'no-cache',
      queryParams: {
        include: ['legalGuardians'].join(),
      },
      headers: { Authorization: `Bearer ${token}` },
    },
    []
  );

  // Additional call needed for getting the country and countryOfBirth for the legalGuardian
  const { get: getLegalGuardian } = useJsonApi(
    `/persons/${legalGuardian?.id}`,
    {
      cachePolicy: 'no-cache',
      queryParams: {
        include: ['country', 'countryOfBirth'].join(),
      },
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  // Logic for setting the right legalGuardian in the state from backend due to the guardian index
  useEffect(() => {
    const {
      required_fields_legal_guardian,
      missing_fields,
    } = minorPassenger.person;
    setLegalGuardianRequiredFields(required_fields_legal_guardian);
    setMissingFields(missing_fields);
    if (dbMinorPerson?.[0]?.legalGuardians[0]) {
      if (guardianIndex === '1') {
        setLegalGuardian(dbMinorPerson[0]?.legalGuardians?.[0]);
      } else {
        setLegalGuardian(dbMinorPerson[0]?.legalGuardians?.[1]);
      }
    }
    // eslint-disable-next-line
  }, [loadedMinorPerson]);

  useEffect(() => {
    // After there is a legal guardian we get additional data
    if (legalGuardian) {
      getLegalGuardian();
    } else if (
      dbMinorPerson?.[0]?.has_single_legal_guardian &&
      guardianIndex === '2'
    ) {
      navigateNext();
    }
    // eslint-disable-next-line
  }, [legalGuardian]);

  const claimId = getIdFromNumber(claimNumber);
  const claim = useBuild('claims', claimId);

  const { navigateNext: navigateToUploadDocument } = useDocumentsNavigate({
    claimId,
    passengerId,
    pageType,
  });

  const { fields, chunkedFields } = useSortFields(
    legalGuardian ? missingFields : legalGuardianRequiredFields,
    nameFields
  );
  const [passengersIds] = useLocalStorageState(`passengersIds-${claimId}`) ?? [
    passengerId,
  ];
  const nextPassengerId =
    passengersIds?.[state?.passengerIndex + 1] ?? undefined;

  /**
   * As this component is used in shareable pages as well we need to correctly
   * build next path as it not always starts with /congratulations.
   * Thus using Regex to make it.
   * Replacing last digit to 2 if
   */

  const nextPassengerPath: string | undefined | Promise<string> =
    requiresTwoGuardians(
      claim.length
        ? claim[0]?.placeOfJurisdiction?.id
        : claim?.placeOfJurisdiction?.id
    ) && Number(guardianIndex) <= 1
      ? pathname.replace(
          translateRoute(routes[steps.Guardian]) + '/1',
          translateRoute(routes[steps.Guardian]) + '/2'
        )
      : resolveNextPassengerRoute(pathname, nextPassengerId);

  async function navigateNext(): void {
    if (pageType !== PageType.Account) {
      const path = await nextPassengerPath;
      if (path) {
        await translatedNavigate(path, {
          state: {
            ...state,
            passengerIndex:
              nextPassengerId &&
              (!requiresTwoGuardians(claim.placeOfJurisdiction?.id) ||
                Number(guardianIndex) >= 2)
                ? state.passengerIndex + 1
                : state.passengerIndex,
          },
        });
      } else {
        await navigateToUploadDocument();
      }
    } else
      navigateToNextStep?.({
        guardian: requiresTwoGuardians(claim.placeOfJurisdiction?.id)
          ? Number(guardianIndex) + 1
          : undefined,
        nextPassengerId,
      });
  }

  function handleSubmit(values, { setSubmitting }): void {
    legalGuardianHandleSubmit({
      minorPassenger,
      legalGuardians: dbMinorPerson[0]?.legalGuardians,
      bearerToken: token,
      legalGuardianValues: values,
      guardianIndex: values.has_single_legal_guardian ? '2' : guardianIndex,
      claim,
      setSubmitting,
      handleError,
      navigateNext,
    });
  }

  useEffect(() => {
    if (legalGuardianRequiredFields.length < 0 || missingFields.length < 0) {
      navigateNext();
    }
    // eslint-disable-next-line
  }, [fields]);

  const isMobile = window.innerWidth < 617;

  return loadedMinorPerson ? (
    <Formik
      onSubmit={handleSubmit}
      validateOnMount={true}
      initialValues={{
        ...legalGuardian,
        ...{
          requiresTwoGuardians: requiresTwoGuardians(
            claim.placeOfJurisdiction?.id
          ),
          has_single_legal_guardian: dbMinorPerson?.[0]
            ?.has_single_legal_guardian
            ? dbMinorPerson?.[0]?.has_single_legal_guardian
            : false,
        },
        country_of_birth_id: legalGuardian?.countryOfBirth?.id,
        country_id: legalGuardian?.country?.id,
      }}
      enableReinitialize={true}
      // eslint-disable-next-line
      validate={validationValues =>
        !validationValues?.has_single_legal_guardian
          ? validateForm(validationValues, fields)
          : {}
      }
    >
      {({ values }): JSX.Element => (
        <Form>
          <Card onlyDesktop={true}>
            <StepTitle
              className="mb-18"
              style={{
                margin: '12px',
                maxWidth: isMobile ? '60%' : 'none',
              }}
            >
              {formatMessage(messages.sharableClaimPageTitle, {
                minor: (
                  <span className="text-black">
                    {minorPassenger.person.first_name}{' '}
                    {minorPassenger.person.last_name}
                  </span>
                ),
              })}
            </StepTitle>
            <Card onlyMobile={true}>
              <h2 className="sm:mx-0 mb-18">
                {formatMessage(messages.sharableClaimPageSubTitle, {
                  // eslint-disable-next-line
                  primary: (...chunks) => (
                    <span className="text-primary-400">{chunks}</span>
                  ),
                  count: guardianIndex,
                })}
              </h2>
              {values.requiresTwoGuardians && guardianIndex === '2' ? (
                <Checkbox
                  name={'has_single_legal_guardian'}
                  className={'sm:max-w-full pt-22'}
                  label={
                    <span
                      className={
                        'font-open-sans text-base font-normal text-gray-800'
                      }
                    >
                      {formatMessage(messages.checkboxSingleParent)}
                    </span>
                  }
                />
              ) : null}
              {!values.has_single_legal_guardian || guardianIndex === '1' ? (
                <PassengerDataForm chunkedFields={chunkedFields}>
                  {legalGuardianRequiredFields?.includes('signature') ? (
                    <>
                      <Field name="signature" component={SignaturePad} />
                    </>
                  ) : null}
                </PassengerDataForm>
              ) : null}
              <TrustBox className="my-8" disableClick={true} />
              {isMobile && (
                <StepNavigation
                  submitButtonMessage={formatMessage(messages.continueButton)}
                  dataLayerPushOnContinue={true}
                  step={steps.Guardian}
                  style={{ margin: '10px' }}
                />
              )}
              {!values.has_single_legal_guardian &&
              legalGuardianRequiredFields?.includes('signature') &&
              isMobile ? (
                <p className="mb-35 mt-15 mx-10 sm:mx-0 text-black">
                  <PermissionDescription
                    placeOfJurisdiction={claim.placeOfJurisdiction}
                    lettersToSign={minorPassenger?.letters_to_sign}
                    passengerId={passengerId}
                    productType={claim?.product_type}
                  />
                </p>
              ) : null}
            </Card>
            {!values.has_single_legal_guardian &&
            legalGuardianRequiredFields?.includes('signature') &&
            !isMobile ? (
              <p className="mb-35 mt-15 mx-10 sm:mx-0 text-black">
                <PermissionDescription
                  placeOfJurisdiction={claim.placeOfJurisdiction}
                  lettersToSign={minorPassenger?.letters_to_sign}
                  passengerId={passengerId}
                  productType={claim?.product_type}
                />
              </p>
            ) : null}
          </Card>
          {!isMobile && (
            <StepNavigation
              submitButtonMessage={formatMessage(messages.continueButton)}
              dataLayerPushOnContinue={true}
              step={steps.Guardian}
            />
          )}
        </Form>
      )}
    </Formik>
  ) : (
    <Loading />
  );
};

export default LegalGuardian;
